import { itemsMaster } from "../Components/PharmacyStocks/StockService";

/** Item master / EAN-13 internal barcode field length */
export const BARCODE_FIELD_LENGTH = 13;

/** Minimum characters to treat Enter as a barcode scan */
export const MIN_BARCODE_LENGTH = 4;

/**
 * Form validation for barcode (required, exactly 13 numeric digits).
 * @returns {string} error message or empty string if valid
 */
export const validateBarcodeField = (value) => {
	const trimmed = String(value ?? "").trim();
	if (!trimmed) return "Barcode is required";
	if (!/^\d+$/.test(trimmed)) return "Barcode must contain numbers only";
	if (trimmed.length !== BARCODE_FIELD_LENGTH) {
		return `Barcode must be exactly ${BARCODE_FIELD_LENGTH} characters`;
	}
	return "";
};

/** Keep only digits, max 13 — for controlled text inputs */
export const sanitizeBarcodeInput = (value) =>
	String(value ?? "")
		.replace(/\D/g, "")
		.slice(0, BARCODE_FIELD_LENGTH);

/** Average ms between keys — faster than this suggests a scanner */
export const SCAN_KEY_INTERVAL_MS = 50;

/**
 * True when keystroke timing matches a USB barcode scanner (fast burst).
 */
export const detectScannerInput = (keyTimestamps) => {
	if (!keyTimestamps || keyTimestamps.length < 2) return false;
	const intervals = [];
	for (let i = 1; i < keyTimestamps.length; i += 1) {
		intervals.push(keyTimestamps[i] - keyTimestamps[i - 1]);
	}
	const avg = intervals.reduce((sum, n) => sum + n, 0) / intervals.length;
	return avg < SCAN_KEY_INTERVAL_MS;
};

/**
 * Heuristic: numeric barcodes (EAN/UPC) are often 8+ digits.
 */
export const isLikelyBarcodeValue = (value) => {
	const trimmed = (value || "").trim();
	if (trimmed.length < MIN_BARCODE_LENGTH) return false;
	return /^[0-9]{8,}$/.test(trimmed);
};

/**
 * Whether Enter on this input should run the barcode handler.
 */
export const shouldHandleAsBarcodeScan = (value, keyTimestamps) => {
	const trimmed = (value || "").trim();
	if (trimmed.length < MIN_BARCODE_LENGTH) return false;
	if (detectScannerInput(keyTimestamps)) return true;
	// Numeric barcodes (EAN/UPC) on Enter without timing — still treat as scan
	return /^[0-9]{8,}$/.test(trimmed);
};

/**
 * Prefer exact barcode match; otherwise first API result.
 */
export const pickBestProductMatch = (products, searchValue) => {
	if (!products?.length) return null;
	const normalized = String(searchValue).trim();
	const exact = products.find(
		(p) => p.barcode && String(p.barcode).trim() === normalized
	);
	return exact || products[0];
};

/**
 * Reuses pharma/get-on-search (itemsMaster).
 */
export const searchProducts = async (search, clinicId) => {
	const response = await itemsMaster({
		search: String(search).trim(),
		clinicId,
	});
	return response.data?.data || [];
};

/**
 * Normalize product for barcode label printing.
 */
export const toBarcodeLabelItem = (product) => ({
	barcode: product?.barcode || "",
	name: product?.brandName || product?.saltName || product?.name || "",
	price:
		product?.salePrice ??
		product?.regularPrice ??
		product?.stockData?.[0]?.unitPricePerMedicine ??
		null,
	currency: product?.currency || "",
});
